import { useContext, useEffect, useRef } from 'react';
import { Outlet, useLocation, useMatches, type UIMatch } from 'react-router';

import { KUDU_BUILD_ID, kuduWritesInFlight } from '@/api/client';

import { AppUpdateContext } from './useAppUpdates';

/**
 * Loads a new build when the user next goes to another page (issue #66).
 *
 * Once useAppUpdates says the server has a new build, the next move to another
 * page of the app becomes a full page load, which the server answers with the
 * new build. The user is leaving the page anyway, so nothing they were doing
 * is lost. It runs after the new page has replaced the old one, so the old
 * page's own "leave this page?" check is already gone.
 *
 * It does not reload:
 * - the page that is open when the update arrives;
 * - between the steps of one page, such as Create Course, whose address moves
 *   on while it keeps what was typed (CHILD_ROUTES_STAY_ON_THIS_PAGE);
 * - while a save the page sent is still on its way, which the reload would cut
 *   off: it waits up to 10 seconds for the server's answer;
 * - when the server cannot be reached: an error page in place of the app
 *   would strand the user, offline or in the installed app;
 * - once the user starts on the new page before all that is known;
 * - a second time from the same build towards the same new build in one tab,
 *   which would mean that build does not reach this device yet: reloading at
 *   every move would only bring the old one back each time.
 */

/** A route handle: the route's child routes are steps of its page, not pages of their own. */
export const CHILD_ROUTES_STAY_ON_THIS_PAGE = { childRoutesStayOnThisPage: true } as const;

const LEFT_BUILD_KEY = 'kudu-update-left-build';
const SERVER_TIMEOUT_MS = 3_000;
const WRITES_LIMIT_MS = 10_000;
const WRITES_POLL_MS = 100;
const USER_ACTIONS = ['keydown', 'pointerdown', 'input'] as const;

function stepsOfOnePage(handle: unknown) {
  return (
    (handle as { childRoutesStayOnThisPage?: unknown } | null | undefined)
      ?.childRoutesStayOnThisPage === true
  );
}

/** The page shown: the address of its route, which the steps of one page share. */
function pageOf(matches: readonly UIMatch[]) {
  const page =
    matches.find((match) => stepsOfOnePage(match.handle)) ?? matches[matches.length - 1];
  return page?.pathname ?? '';
}

/**
 * Whether every write on its way has been answered: true once none is left,
 * false when some still are after `limitMs`.
 */
export async function writesSettle(writesInFlight: () => number, limitMs = WRITES_LIMIT_MS) {
  const until = Date.now() + limitMs;
  while (writesInFlight() > 0) {
    if (Date.now() >= until) return false;
    await new Promise((resolve) => setTimeout(resolve, WRITES_POLL_MS));
  }
  return true;
}

/**
 * Whether the server answers for this page now, past every cache. Offline, or
 * with no answer within 3 seconds, it does not.
 */
export async function serverAnswers(
  url: string,
  fetchPage: (url: string, init: RequestInit) => Promise<Response> = (input, init) =>
    fetch(input, init),
  timeoutMs = SERVER_TIMEOUT_MS,
) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchPage(url, {
      cache: 'no-store',
      credentials: 'same-origin',
      method: 'HEAD',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// What this tab set off from and for last time: { from, to } builds.
function setOffBefore(newBuild: string) {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(LEFT_BUILD_KEY);
  } catch {
    // A tab that cannot remember where it has been does not reload at all.
    return true;
  }
  try {
    const left = JSON.parse(stored ?? 'null') as { from?: unknown; to?: unknown } | null;
    return left?.from === KUDU_BUILD_ID && left?.to === newBuild;
  } catch {
    return false;
  }
}

function noteSettingOff(newBuild: string) {
  try {
    sessionStorage.setItem(LEFT_BUILD_KEY, JSON.stringify({ from: KUDU_BUILD_ID, to: newBuild }));
    return true;
  } catch {
    return false;
  }
}

function reloadPage() {
  window.location.reload();
}

interface LoadNewVersionOnPageChangeProps {
  reachable?: (url: string) => Promise<boolean>;
  reload?: () => void;
  writesInFlight?: () => number;
  writesLimitMs?: number;
}

export function LoadNewVersionOnPageChange({
  reachable = serverAnswers,
  reload = reloadPage,
  writesInFlight = kuduWritesInFlight,
  writesLimitMs = WRITES_LIMIT_MS,
}: LoadNewVersionOnPageChangeProps) {
  const newBuild = useContext(AppUpdateContext);
  const location = useLocation();
  const page = pageOf(useMatches());
  const shownPage = useRef(page);

  useEffect(() => {
    if (shownPage.current === page) return undefined;
    shownPage.current = page;
    if (newBuild === null || setOffBefore(newBuild)) return undefined;

    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    const stopWatching = () => {
      USER_ACTIONS.forEach((type) => window.removeEventListener(type, cancel, true));
    };
    USER_ACTIONS.forEach((type) => window.addEventListener(type, cancel, true));

    const url = new URL(
      `${location.pathname}${location.search}${location.hash}`,
      window.location.origin,
    ).href;
    void (async () => {
      const answered = (await writesSettle(writesInFlight, writesLimitMs)) && !cancelled;
      const answers = answered && (await reachable(url));
      stopWatching();
      if (answers && !cancelled && noteSettingOff(newBuild)) reload();
    })();

    return () => {
      cancelled = true;
      stopWatching();
    };
    // The location is the one this page change brought; a later change of
    // its query string alone is not another page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, newBuild]);

  return <Outlet />;
}
